// src/lib/themeColors.ts
/**
 * Theme-aware color system for dark mode support
 * Provides consistent colors across the numberline canvas and all visual elements
 */

export interface ThemeColors {
  // Axis and primary structure
  axis: string;
  
  // Tick colors by hierarchy level
  ticks: {
    level0: string;  // Primary ticks
    level1: string;  // Halves
    level2: string;  // Quarters
    level3: string;  // Eighths/tenths
    level4: string;  // Finest subdivisions
  };
  
  // Labels and text
  labelText: string;
  labelSecondary: string;
  
  // Backgrounds
  canvasBackground: string;
  menuBackground: string;
  menuBorder: string;
  menuText: string;
  menuTextSecondary: string;
  
  // Placeholders (hidden labels)
  placeholderFill: string;
  placeholderStroke: string;
  
  // Constants overlay
  constantMarker: string;
  constantMarkerStroke: string;
  constantLine: string;
  constantLabelBg: string;
  constantLabelBorder: string;
  constantLabelText: string;
  
  // Secondary line (green scheme)
  secondaryAxis: string;
  secondaryTick: string;
  secondaryTickLight: string;
  secondaryLabel: string;
  secondaryPlaceholderFill: string;
  secondaryPlaceholderStroke: string;
  alignmentLine: string;
  
  // Jump indicators (keep consistent colors for semantic meaning)
  jumpPositive: string;
  jumpNegative: string;
  jumpHandleStroke: string;
  
  // Measurement tool
  measurementLine: string;
  measurementText: string;
  
  // Selection and interaction
  selectionHighlight: string;
  hoverHighlight: string;
}

/**
 * Get theme-aware colors based on dark mode state
 */
export const getThemeColors = (isDark: boolean): ThemeColors => ({
  // Axis and primary structure
  axis: isDark ? '#9CA3AF' : '#374151',
  
  // Tick colors by hierarchy level
  // In dark mode, we invert the contrast - primary ticks are brightest
  ticks: {
    level0: isDark ? '#E5E7EB' : '#1F2937',  // Primary: brightest in dark, darkest in light
    level1: isDark ? '#D1D5DB' : '#374151',  // Halves
    level2: isDark ? '#9CA3AF' : '#6B7280',  // Quarters
    level3: isDark ? '#6B7280' : '#9CA3AF',  // Eighths/tenths
    level4: isDark ? '#4B5563' : '#D1D5DB',  // Finest: dimmest in dark, lightest in light
  },
  
  // Labels and text
  labelText: isDark ? '#F9FAFB' : '#1F2937',
  labelSecondary: isDark ? '#D1D5DB' : '#6B7280',
  
  // Backgrounds
  canvasBackground: isDark ? '#1F2937' : '#FFFFFF',
  menuBackground: isDark ? '#374151' : '#FFFFFF',
  menuBorder: isDark ? '#4B5563' : '#D1D5DB',
  menuText: isDark ? '#F9FAFB' : '#1F2937',
  menuTextSecondary: isDark ? '#9CA3AF' : '#6B7280',
  
  // Placeholders (hidden labels)
  placeholderFill: isDark ? '#374151' : '#F3F4F6',
  placeholderStroke: isDark ? '#6B7280' : '#9CA3AF',
  
  // Constants overlay - blue scheme, adjusted for visibility
  constantMarker: '#0EA5E9',  // Keep consistent blue marker
  constantMarkerStroke: isDark ? '#38BDF8' : '#0284C7',
  constantLine: isDark ? '#38BDF8' : '#0EA5E9',
  constantLabelBg: isDark ? '#1E3A5F' : '#FFFFFF',
  constantLabelBorder: isDark ? '#38BDF8' : '#0EA5E9',
  constantLabelText: isDark ? '#E0F2FE' : '#0F172A',
  
  // Secondary line (green scheme)
  secondaryAxis: isDark ? '#34D399' : '#059669',
  secondaryTick: isDark ? '#34D399' : '#059669',
  secondaryTickLight: isDark ? '#6EE7B7' : '#10B981',
  secondaryLabel: isDark ? '#A7F3D0' : '#059669',
  secondaryPlaceholderFill: isDark ? '#064E3B' : '#D1FAE5',
  secondaryPlaceholderStroke: isDark ? '#34D399' : '#10B981',
  alignmentLine: isDark ? '#4B5563' : '#D1D5DB',
  
  // Jump indicators - semantic colors maintained for meaning
  jumpPositive: '#22c55e',  // Green for positive (same in both modes)
  jumpNegative: '#ef4444',  // Red for negative (same in both modes)
  jumpHandleStroke: isDark ? '#1F2937' : '#FFFFFF',
  
  // Measurement tool
  measurementLine: '#3b82f6',  // Blue for measurement
  measurementText: isDark ? '#93C5FD' : '#1E40AF',
  
  // Selection and interaction
  selectionHighlight: '#3B82F6',
  hoverHighlight: isDark ? '#60A5FA' : '#2563EB',
});

/**
 * Get tick style including color based on hierarchical level
 */
export interface TickStyle {
  height: number;
  strokeWidth: number;
  color: string;
}

const TICK_HEIGHTS = [16, 12, 8, 5, 4];
const TICK_STROKE_WIDTHS = [2, 1.5, 1, 0.75, 0.5];

export const getTickStyleByLevel = (
  level: number, 
  isOrigin: boolean, 
  isCustom: boolean, 
  isDark: boolean
): TickStyle => {
  const colors = getThemeColors(isDark);
  
  // Origin tick - red color for zero point
  if (isOrigin) {
    return { height: 16, strokeWidth: 2, color: '#DC2626' };
  }
  
  // Custom tick - purple color for user-added ticks
  if (isCustom) {
    return { height: 16, strokeWidth: 2, color: '#7c3aed' };
  }
  
  // Regular hierarchical ticks
  const tickColors = [
    colors.ticks.level0,
    colors.ticks.level1,
    colors.ticks.level2,
    colors.ticks.level3,
    colors.ticks.level4,
  ];
  
  const clampedLevel = Math.min(level, 4);
  
  return {
    height: TICK_HEIGHTS[clampedLevel] ?? 4,
    strokeWidth: TICK_STROKE_WIDTHS[clampedLevel] ?? 0.5,
    color: tickColors[clampedLevel] ?? colors.ticks.level4,
  };
};









